import type { FC, MouseEventHandler, ReactNode } from 'react'

import { ArrowUpRight, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TransitionLink } from '@/components/generals/transition-link'

interface ButtonProps {
    children: string
    href?: string
    variant?: 'primary' | 'secondary' | 'ghost'
    size?: 'md' | 'lg'
    type?: 'button' | 'submit'
    disabled?: boolean
    loading?: boolean
    className?: string
    onClick?: MouseEventHandler<HTMLElement>
}

const VARIANTS = {
    primary: 'bg-foreground text-background hover:bg-white',
    secondary: 'border border-line-strong bg-white/[0.03] text-foreground backdrop-blur-md hover:border-foreground/40 hover:bg-white/[0.08]',
    ghost: 'text-foreground hover:bg-white/[0.06]'
}

const ICON_BOX = {
    primary: 'border-l border-black/10',
    secondary: 'border-l border-line-strong',
    ghost: ''
}

export const Button: FC<ButtonProps> = ({
    children,
    href,
    variant = 'primary',
    size = 'md',
    type = 'button',
    disabled = false,
    loading = false,
    className,
    onClick
}): ReactNode => {
    const classes = cn(
        'group relative inline-flex items-stretch overflow-hidden text-[0.9375rem] font-medium tracking-[-0.01em] transition-colors duration-500 ease-expo select-none',
        size === 'md' ? 'h-12' : 'h-14 text-base',
        VARIANTS[variant],
        (disabled || loading) && 'pointer-events-none opacity-60',
        className
    )

    const content = (
        <>
            <span className={cn('flex items-center', size === 'md' ? 'px-5' : 'px-6')}>
                <span className='relative block overflow-hidden'>
                    <span className='block transition-transform duration-500 ease-expo group-hover:-translate-y-full'>
                        {children}
                    </span>

                    <span
                        aria-hidden='true'
                        className='absolute inset-0 block translate-y-full transition-transform duration-500 ease-expo group-hover:translate-y-0'
                    >
                        {children}
                    </span>
                </span>
            </span>

            <span className={cn('flex aspect-square items-center justify-center', ICON_BOX[variant])}>
                {loading
                    ? <LoaderCircle className='size-4 animate-spin' />
                    : <ArrowUpRight className='size-4 transition-transform duration-500 ease-expo group-hover:rotate-45' />}
            </span>
        </>
    )

    if (href) {
        if (href.startsWith('mailto:') || href.startsWith('http')) {
            return (
                <a href={href} className={classes} onClick={onClick}>
                    {content}
                </a>
            )
        }

        return (
            <TransitionLink href={href} className={classes} onClick={onClick}>
                {content}
            </TransitionLink>
        )
    }

    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={classes}
            onClick={onClick}
        >
            {content}
        </button>
    )
}
